import Creatives from 'Components/campaigns/Creatives';
import Stepper from 'Components/Common/Stepper'
import { PRIVILEGE } from 'constants/privilege';
import withAccessControl from 'hoc/withAccessControl';
import { usePrivileges } from 'hooks/useAuth';
import useDocumentTitle from 'hooks/useDocumentTitle';
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardBody, Form, Spinner } from 'reactstrap';
import { updateCampaignCreatives } from 'services/api/campaign';
import { fetchCampaignCreatives } from 'slices/campaigns/creatives/thunk';
import { markCampaignDataExpired } from 'slices/campaigns/reducer';
import { sendCampaignForApprovalRequest } from 'slices/campaigns/thunk';
import { AppDispatch, RootState } from 'store';
import { getErrorMessage } from 'utils/helpers';

const steps = [
    { label: "Campaign Details", completed: true },
    { label: "Campaign Milestones", completed: true },
    { label: "Influencer Criteria", completed: true },
    { label: "Creatives", active: true }
];

const CampaignCreatives = () => {

    useDocumentTitle("Creatives - Campaign");

    const { campaignId } = useParams();
    const privileges = usePrivileges();

    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);

    const campaignCreatives = useSelector((state: RootState) => state.campaignCreatives.campaigns[campaignId!])

    const campaign = campaignCreatives?.campaign ?? null;
    const gallery = campaignCreatives?.gallery ?? [];

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();

    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [campaignHeroImage, setCampaignHeroImage] = useState<File | null>(null);

    useEffect(() => {
        if (campaignId) {
            dispatch(fetchCampaignCreatives({ campaignId }));
        }
    }, [dispatch, campaignId])


    const submitCreatives = async () => {
        if (!campaignId) return;
        try {
            if(!privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT))
                throw new Error("You are not authorized to perform this action!");

            setSaving(true);
            const formData = new FormData();
            if (campaignHeroImage) {
                formData.append('hero_img', campaignHeroImage);
            }
            const response = await updateCampaignCreatives(selectedAgency?.uuid!, campaignId, formData);
            dispatch(markCampaignDataExpired({campaignId}));
            if (response) {
                navigate('/campaign?status=draft');
            }
        } catch (error) {
            console.log(error);
            toast.error(getErrorMessage(error, 'Failed to submit creatives'));
        } finally {
            setSaving(false);
        }
    }

    const submitAndRequestApproval = async () => {
        if(!campaignId) return;
        try {
            if(!privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT))
                throw new Error("You are not authorized to perform this action!");

            setSubmitting(true);
            const formData = new FormData();
            if (campaignHeroImage) {
                formData.append('hero_img', campaignHeroImage);
            }
            await updateCampaignCreatives(selectedAgency?.uuid!, campaignId, formData);
            await dispatch(sendCampaignForApprovalRequest({campaignId}))
            dispatch(markCampaignDataExpired({campaignId}));
            navigate('/campaign?status=pending');
        } catch (error) {
            console.log(error);
            toast.error(getErrorMessage(error, 'Failed to submit creatives'));
        } finally {
            setSubmitting(false);
        }


    }

    return (
        <div className="page-content">
            <div className="page-content-inner page-spacing">
                <div className="d-flex w-100 justify-content-between align-items-center pt-1 pb-5">
                    <Stepper steps={steps} />
                </div>
                <Form className='needs-validation' onSubmit={(e) => {
                    e.preventDefault();
                    submitCreatives();
                    return false;
                }}>
                    <Card>
                        <CardBody>
                            <Creatives
                                campaignId={campaignId!}
                                campaignHeroImage={campaign?.hero_img_url ?? null}
                                gallery={gallery}
                                onHeroImageChange={(newHeroImage) => setCampaignHeroImage(newHeroImage)}
                            />
                        </CardBody>
                    </Card>
                    <div className="d-flex justify-content-between mb-4">
                        <Link to={{ pathname: `/campaign/${campaignId}/milestone` }} type='button' className="theme-btn theme-btn-lg btn-primary-outlined"><i className='bx fs-4 bx-left-arrow-alt me-2' ></i>Back</Link>

                        <div className='d-flex gap-4'>
                            <button type='submit' disabled={saving || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-primary-light form-continue-btn">
                                <React.Fragment>
                                    Save as draft
                                    {
                                        saving ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                                    }
                                </React.Fragment>
                            </button>
                            <button type='button' onClick={submitAndRequestApproval} disabled={submitting || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-gradient form-continue-btn">
                                <React.Fragment>
                                    Submit for Approval
                                    {
                                        submitting ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                                    }
                                </React.Fragment>
                            </button>
                        </div>

                    </div>
                </Form>
            </div>
        </div>
    )
}

export default withAccessControl(CampaignCreatives, PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)